import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';

import { Close } from '@design-systems/icons';
// eslint-disable-next-line no-restricted-imports
import { useTransition, animated } from 'react-spring';
import usePrefersReducedMotion from '@mc/hooks/usePrefersReducedMotion';
import { useDsTranslateMessage } from '@mc/wink/internationalization/useDsTranslateMessage';
import IconButton from '../IconButton';
import stylesheet from './Toast.css';

export type ToastProps = {
  action?: React.ReactElement;
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  onClose?: $TSFixMeFunction;
  tunnelId?: string;
  duration?: number;
  /**
   * @deprecated
   * Will be removed in the future.
   */
  inline?: boolean;
};

// Internal Message component for use in stories and inline rendering
export const Message = React.forwardRef<
  $TSFixMe,
  Omit<ToastProps, 'tunnelId' | 'inline' | 'duration'>
>(function Message(
  { action, className, children, icon, onClose, ...rest },
  forwardedRef,
) {
  const classList = className;

  // Translation for default icon button label text
  const iconButtonLabelText = useDsTranslateMessage({
    id: 'mcds.toast.icon_button_label_text',
    defaultMessage: 'Dismiss notification',
  });

  return (
    <div className={stylesheet.root}>
      <div className={classList} ref={forwardedRef} {...rest}>
        <div className={stylesheet.wrapper}>
          <div className={stylesheet.leftContent}>
            <div className={cx(stylesheet.icon, stylesheet.align)}>
              {icon && <div>{icon}</div>}
              <div className={cx(stylesheet.textContent, stylesheet.copy)}>
                {children}
              </div>
            </div>
            <div
              className={icon && stylesheet.actionContent}
              data-colorscheme="dark"
            >
              {action}
            </div>
          </div>
          <div
            className={cx(stylesheet.rightContent, stylesheet.align)}
            data-colorscheme="dark"
          >
            <IconButton
              label={iconButtonLabelText}
              onClick={onClose}
              icon={<Close />}
              size="small"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

/** An alert in response to user actions. */
const Toast = React.forwardRef<$TSFixMe, ToastProps>(function Toast(
  {
    action,
    className,
    children,
    icon,
    onClose,
    tunnelId = 'portal-notification',
    inline = false,
    duration,
    ...rest
  },
  forwardedRef,
) {
  const classList = className;
  const prefersReducedMotion = usePrefersReducedMotion();
  const [showToast, setShowToast] = useState(true);
  const mountNodeRef = useRef(document.getElementById(tunnelId));

  const onKeyDownHandler = useCallback(
    ({ key, defaultPrevented }: $TSFixMe) => {
      if (!defaultPrevented && key === 'Escape') {
        // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        onClose();
      }
    },
    [onClose],
  );

  const setOnClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (showToast && duration) {
      const timer = setTimeout(() => {
        // move setShowToast to line 59 if toast needs to be closed by clicking x
        setShowToast(false);
        setOnClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [showToast, setOnClose, duration]);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDownHandler, { capture: true });
    return () => {
      document.removeEventListener('keydown', onKeyDownHandler, {
        capture: true,
      });
    };
  }, [onKeyDownHandler]);

  const message = showToast && (
    <Message
      action={action}
      className={classList}
      ref={forwardedRef}
      icon={icon}
      onClose={setOnClose}
      {...rest}
    >
      {children}
    </Message>
  );

  const animationConfig = {
    from: { opacity: 0, transform: 'translate3d(0%,90%,0)' },
    enter: { opacity: 1, transform: 'translate3d(0,0%,0)' },
    config: {
      mass: 1,
      tension: 620,
      friction: 28,
    },
  };

  const transitions = useTransition(
    true,
    prefersReducedMotion ? {} : animationConfig,
  );

  // This is a workaround to make the toast visible in the DOM tree. Without placing the toast in document.body as a fallback, the toast will not be visible in the DOM tree.
  const getMountNode = () => {
    const tunnelElement = mountNodeRef.current;
    return tunnelElement || document.body;
  };

  return !inline
    ? createPortal(
        transitions((style) => (
          <animated.div className="mcds-toast" style={style}>
            {message}
          </animated.div>
        )),
        getMountNode(),
      )
    : message;
});

export default Toast;
